/**
 * Frontend mirror of apps/api/src/lib/permissions.ts ROLE_PERMISSIONS.
 * Used by the read-only "Roles y Permisos" screen. Keep in sync with the backend.
 */

import type { AnyRole } from "./roles";

export const PERMISSIONS = {
  // ── Nav visibility (controls whether a role sees the tab) ────────────────────────────
  PROGRAMACION_VIEW:    "programacion:view",
  MONITOR_VIEW:         "monitor:view",
  DISPONIBILIDAD_VIEW:  "disponibilidad:view",
  REPORTES_VIEW:        "reportes:view",
  CLIENTS_READ:         "clients:read",
  PLANTS_READ:          "plants:read",
  PERSONNEL_READ:       "personnel:read",
  CATALOGS_READ:        "catalogs:read",
  OPTIONALS_READ:       "optionals:read",
  AUDIT_READ:           "audit:read",
  AUDIT_DETAIL:         "audit:detail",
  // ── Services ──────────────────────────────────────────────────────────────
  SERVICES_READ:        "services:read",
  SERVICES_WRITE:       "services:write",
  SERVICES_ASSIGN:      "services:assign",
  SERVICES_LEAVE_TIME_WRITE: "services:leave_time_write",
  SERVICES_OPERATE:     "services:operate",
  // ── Master data ───────────────────────────────────────────────────────────
  CLIENTS_WRITE:        "clients:write",
  LOCATIONS_WRITE:      "locations:write",
  VEHICLES_READ:        "vehicles:read",
  VEHICLES_WRITE:       "vehicles:write",
  PERSONNEL_WRITE:      "personnel:write",
  CATALOGS_WRITE:       "catalogs:write",
  OPTIONALS_WRITE:      "optionals:write",
  // ── Certifications ────────────────────────────────────────────────────────
  CERTIFICATIONS_READ:  "certifications:read",
  CERTIFICATIONS_WRITE: "certifications:write",
  // ── Dashboard ─────────────────────────────────────────────────────────────
  DASHBOARD_OPS:        "dashboard:ops",
  DASHBOARD_EXECUTIVE:  "dashboard:executive",
  // ── System ────────────────────────────────────────────────────────────────
  USERS_READ:          "users:read",
  USERS_WRITE:         "users:write",
  USERS_RESET:         "users:reset",
  // Reading the flag list needs no permission — see apps/api/src/routes/feature_flags.ts.
  FEATURE_FLAGS:        "feature_flags:manage",
  FEEDBACK_WRITE:       "feedback:write",
  FEEDBACK_READ:        "feedback:read",
  FEEDBACK_MANAGE:      "feedback:manage",
  FEEDBACK_DELETE:      "feedback:delete",
  DOCUMENT_ALERTS:      "document_alerts:manage",
  ROLES_VIEW:           "roles_perms:view",
  ROLES_MANAGE:         "roles_perms:manage",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ROLE_PERMISSIONS: Record<AnyRole, readonly (Permission | "*")[]> = {
  superadmin: ["*"],
  admin: [
    PERMISSIONS.PROGRAMACION_VIEW, PERMISSIONS.MONITOR_VIEW, PERMISSIONS.DISPONIBILIDAD_VIEW, PERMISSIONS.REPORTES_VIEW,
    PERMISSIONS.CLIENTS_READ, PERMISSIONS.PLANTS_READ, PERMISSIONS.PERSONNEL_READ, PERMISSIONS.CATALOGS_READ,
    PERMISSIONS.OPTIONALS_READ,
    PERMISSIONS.AUDIT_READ, PERMISSIONS.AUDIT_DETAIL,
    PERMISSIONS.SERVICES_READ, PERMISSIONS.SERVICES_WRITE, PERMISSIONS.SERVICES_ASSIGN, PERMISSIONS.SERVICES_LEAVE_TIME_WRITE, PERMISSIONS.SERVICES_OPERATE,
    PERMISSIONS.CLIENTS_WRITE, PERMISSIONS.LOCATIONS_WRITE,
    PERMISSIONS.VEHICLES_READ, PERMISSIONS.VEHICLES_WRITE, PERMISSIONS.PERSONNEL_WRITE, PERMISSIONS.CATALOGS_WRITE,
    PERMISSIONS.OPTIONALS_WRITE,
    PERMISSIONS.CERTIFICATIONS_READ, PERMISSIONS.CERTIFICATIONS_WRITE,
    PERMISSIONS.DASHBOARD_OPS, PERMISSIONS.DASHBOARD_EXECUTIVE,
    PERMISSIONS.FEEDBACK_READ, PERMISSIONS.FEEDBACK_WRITE, PERMISSIONS.FEEDBACK_MANAGE, PERMISSIONS.FEEDBACK_DELETE,
    PERMISSIONS.DOCUMENT_ALERTS,
  ],
  gerencia: [
    PERMISSIONS.PROGRAMACION_VIEW, PERMISSIONS.MONITOR_VIEW, PERMISSIONS.DISPONIBILIDAD_VIEW, PERMISSIONS.REPORTES_VIEW,
    PERMISSIONS.AUDIT_READ, PERMISSIONS.AUDIT_DETAIL,
    PERMISSIONS.SERVICES_READ,
    PERMISSIONS.SERVICES_ASSIGN, PERMISSIONS.SERVICES_LEAVE_TIME_WRITE, PERMISSIONS.SERVICES_OPERATE,
    PERMISSIONS.VEHICLES_READ,
    PERMISSIONS.CERTIFICATIONS_READ,
    PERMISSIONS.DASHBOARD_OPS, PERMISSIONS.DASHBOARD_EXECUTIVE,
    PERMISSIONS.FEEDBACK_WRITE,
  ],
  logistica: [
    PERMISSIONS.PROGRAMACION_VIEW, PERMISSIONS.MONITOR_VIEW, PERMISSIONS.DISPONIBILIDAD_VIEW,
    PERMISSIONS.CLIENTS_READ, PERMISSIONS.PLANTS_READ,
    PERMISSIONS.AUDIT_READ, PERMISSIONS.AUDIT_DETAIL,
    PERMISSIONS.SERVICES_READ, PERMISSIONS.SERVICES_WRITE, PERMISSIONS.SERVICES_ASSIGN, PERMISSIONS.SERVICES_LEAVE_TIME_WRITE, PERMISSIONS.SERVICES_OPERATE,
    PERMISSIONS.CLIENTS_WRITE, PERMISSIONS.LOCATIONS_WRITE,
    PERMISSIONS.VEHICLES_READ, PERMISSIONS.VEHICLES_WRITE,
    PERMISSIONS.CERTIFICATIONS_READ,
    PERMISSIONS.DASHBOARD_OPS,
    PERMISSIONS.FEEDBACK_WRITE,
  ],
  comercial: [
    PERMISSIONS.REPORTES_VIEW,
    PERMISSIONS.SERVICES_READ,
    PERMISSIONS.DASHBOARD_EXECUTIVE,
    PERMISSIONS.FEEDBACK_WRITE,
  ],
  chofer: [
    PERMISSIONS.SERVICES_READ, PERMISSIONS.SERVICES_OPERATE,
  ],
};

export function roleHasPermission(role: AnyRole, permission: Permission): boolean {
  const perms = ROLE_PERMISSIONS[role] ?? [];
  return perms.includes("*") || perms.includes(permission);
}
